import {defineStore} from 'pinia'
import {computed, ref} from 'vue'
import * as Sentry from '@sentry/vue'
import {OnboardingStepType} from '@toolify/server/src/enum/OnboardingStepType'
import {OnboardingEmployeeRangeType} from '@toolify/server/src/models/mongoose/WorkspaceModel/enum/OnboardingEmployeeRangeType'
import {OnboardingTeamRangeType} from '@toolify/server/src/models/mongoose/WorkspaceModel/enum/OnboardingTeamRangeType'
import {OnboardingCompanyCoreAreaType} from '@toolify/server/src/models/mongoose/WorkspaceModel/enum/OnboardingCompanyCoreAreaType'
import {OnboardingSolutionType} from '@toolify/server/src/models/mongoose/WorkspaceModel/enum/OnboardingSolutionType'
import {OnboardingUserRoleType} from '@toolify/server/src/models/mongoose/UserModel/enum/OnboardingUserRoleType'
import {OnboardingDiscoverSourceType} from '@toolify/server/src/models/mongoose/UserModel/enum/OnboardingDiscoverSourceType'
import {useWorkspaceStore} from '@toolify/client/src/stores/WorkspaceStore/useWorkspaceStore'
import {useWorkspaceApiStore} from '@toolify/client/src/stores/api/WorkspaceApiStore/useWorkspaceApiStore'
import {
  useWorkspaceInvitationApiStore,
} from '@toolify/client/src/stores/api/WorkspaceInvitationApiStore/useWorkspaceInvitationApiStore'
import {
  IWorkspaceInviteResult,
} from '@toolify/server/src/controllers/WorkspaceInvitationController/validators/CreateWorkspaceInvitation/types/ICreateWorkspaceInvitationRequestInterfaces'
import {useMeApiStore} from '@toolify/client/src/stores/api/MeApiStore/useMeApiStore'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {
  isUserLevelOnboardingStep,
  onboardingStepOrder,
} from '@toolify/client/src/modules/onboarding/enums/onboardingStepMetadataMap'
import {
  hasUserOnboardingData,
  resolveOnboardingState,
} from '@toolify/client/src/modules/onboarding/functions/resolveOnboardingState'
import {i18n} from '@toolify/client/src/setup/i18n'
import {LocaleType} from '@toolify/server/src/services/UserSettingsService/strategies/LocaleUserSettingStrategy/enum/LocaleType'
import {IPendingOnboardingPlan} from '@toolify/server/src/models/mongoose/WorkspaceModel/types/IPendingOnboardingPlan'
import {IOnboardingWorkspacePlanGroup} from '@toolify/server/src/models/mongoose/WorkspaceModel/types/IOnboardingWorkspacePlanGroup'
import {PermissionType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/PermissionType'
import {DateFormatType} from '@toolify/server/src/services/UserSettingsService/strategies/DateFormatUserSettingStrategy/enum/DateFormatType'
import {useFileUpload} from '@toolify/client/src/modules/files/upload/useFileUpload'
import {FileUploadTargetType} from '@toolify/client/src/modules/files/upload/types/FileUploadTargetType'
import {
  IUpdateWorkspaceLogoRequestInterfaces,
} from '@toolify/server/src/controllers/WorkspaceController/validators/UpdateWorkspaceLogo/types/IUpdateWorkspaceLogoRequestInterfaces'

interface IPersistOptions {
  syncUser?: boolean
  onRecomputed?: () => void
}

export const useOnboardingStore = defineStore('onboarding', () => {
  const workspaceStore = useWorkspaceStore()
  const workspaceApiStore = useWorkspaceApiStore()
  const workspaceInvitationApiStore = useWorkspaceInvitationApiStore()
  const meApiStore = useMeApiStore()
  const authStore = useAuthStore()
  const {uploadFile} = useFileUpload()
  const {t} = i18n.global

  const pendingSteps = ref<OnboardingStepType[]>([])
  const isComplete = ref(false)
  const isKnown = ref(false)
  const isLoading = ref(false)
  const loadedWorkspaceId = ref<string | null>(null)
  const saveErrorMessage = ref<string | null>(null)
  const successMessage = ref<string | null>(null)
  const revisitedStep = ref<OnboardingStepType | null>(null)
  const userStepsInRun = ref<OnboardingStepType[]>([])
  const canEditWorkspace = ref<boolean | null>(null)
  const isPersisting = ref(false)
  let successMessageTimeout: ReturnType<typeof setTimeout> | null = null

  function showSuccessMessage(message: string): void {
    successMessage.value = message
    if(successMessageTimeout) {
      clearTimeout(successMessageTimeout)
    }
    successMessageTimeout = setTimeout(() => {
      successMessage.value = null
    }, 4000)
  }

  const firstPendingStep = computed<OnboardingStepType | null>(() => {
    return pendingSteps.value[0] ?? null
  })

  function isStepDone(step: OnboardingStepType): boolean {
    return !pendingSteps.value.includes(step)
  }

  const visibleSteps = computed(() => {
    return onboardingStepOrder.filter(step => isUserLevelOnboardingStep(step)
      ? userStepsInRun.value.includes(step)
      : canEditWorkspace.value !== false)
  })

  const doneSteps = computed(() => visibleSteps.value.filter(step => isStepDone(step)))

  const currentStep = computed<OnboardingStepType | null>(() => {
    const revisited = revisitedStep.value
    if(revisited && isStepDone(revisited)) {
      return revisited
    }
    return firstPendingStep.value
  })

  const currentStepIndex = computed(() => {
    return currentStep.value ? visibleSteps.value.indexOf(currentStep.value) : visibleSteps.value.length
  })

  const isRevisiting = computed(() => {
    return currentStep.value !== null && currentStep.value !== firstPendingStep.value
  })

  const isFinalStep = computed(() => {
    return pendingSteps.value.length === 1 && !isRevisiting.value
  })

  const canNavigate = computed(() => {
    return firstPendingStep.value !== null && !isPersisting.value
  })

  function canGoToStep(step: OnboardingStepType): boolean {
    if(!canNavigate.value || step === currentStep.value || !visibleSteps.value.includes(step)) {
      return false
    }
    return step === firstPendingStep.value || isStepDone(step)
  }

  const previousStep = computed<OnboardingStepType | null>(() => {
    return visibleSteps.value
      .slice(0, Math.max(currentStepIndex.value, 0))
      .reverse()
      .find(step => canGoToStep(step)) ?? null
  })

  const canGoBack = computed(() => previousStep.value !== null)

  function goToStep(step: OnboardingStepType): void {
    if(!canGoToStep(step)) {
      return
    }
    saveErrorMessage.value = null
    revisitedStep.value = step === firstPendingStep.value ? null : step
  }

  function goBack(): void {
    if(previousStep.value) {
      goToStep(previousStep.value)
    }
  }

  function goForward(): void {
    if(!isRevisiting.value || isPersisting.value) {
      return
    }
    saveErrorMessage.value = null
    advanceFromStep(currentStep.value)
  }

  function advanceFromStep(savedStep: OnboardingStepType | null): void {
    if(!savedStep || isComplete.value) {
      revisitedStep.value = null
      return
    }
    const nextStep = visibleSteps.value[visibleSteps.value.indexOf(savedStep) + 1] ?? null
    revisitedStep.value = nextStep && isStepDone(nextStep) ? nextStep : null
  }

  function canEditCurrentWorkspace(): boolean | null {
    if(!workspaceStore.workspacePermissionMap) {
      return null
    }
    return workspaceStore.hasPermission(PermissionType.updateWorkspace)
  }

  function recomputeFromStores(): void {
    canEditWorkspace.value = canEditCurrentWorkspace()
    const state = resolveOnboardingState(workspaceStore.currentWorkspace, authStore.currentUser, {
      canEditWorkspace: canEditWorkspace.value,
    })
    const workspaceId = workspaceStore.currentWorkspace?.id ?? null
    if(workspaceId !== loadedWorkspaceId.value) {
      userStepsInRun.value = state.pendingSteps.filter(step => isUserLevelOnboardingStep(step))
    }
    if(workspaceId !== loadedWorkspaceId.value || state.isComplete) {
      revisitedStep.value = null
    }
    pendingSteps.value = state.pendingSteps
    isComplete.value = state.isComplete
    isKnown.value = state.isKnown
    loadedWorkspaceId.value = workspaceId
  }

  async function refreshState(onRecomputed?: () => void): Promise<void> {
    const workspaceName = workspaceStore.currentWorkspace?.name
    if(!workspaceName) {
      recomputeFromStores()
      onRecomputed?.()
      return
    }
    isLoading.value = true
    try {
      const response = await workspaceApiStore.getWorkspaceByName(workspaceName)
      workspaceStore.currentWorkspace = response.workspace
      if(!hasUserOnboardingData(authStore.currentUser)) {
        await authStore.refetchCurrentEntity()
      }
      recomputeFromStores()
      onRecomputed?.()
    } finally {
      isLoading.value = false
    }
  }

  async function persistWithResult<T>(action: () => Promise<T>, fallback: T): Promise<T> {
    saveErrorMessage.value = null
    try {
      return await action()
    } catch(error) {
      saveErrorMessage.value = t('onboarding.error.saveFailed')
      Sentry.captureException(error)
      return fallback
    }
  }

  async function persist(action: () => Promise<void>, options?: IPersistOptions): Promise<boolean> {
    isPersisting.value = true
    try {
      return await persistWithResult<boolean>(async () => {
        await action()
        if(options?.syncUser) {
          await authStore.refetchCurrentEntity()
          recomputeFromStores()
          options.onRecomputed?.()
          return true
        }
        await refreshState(options?.onRecomputed)
        return true
      }, false)
    } finally {
      isPersisting.value = false
    }
  }

  async function persistStep(action: () => Promise<void>, options?: {syncUser?: boolean}): Promise<void> {
    const savedStep = currentStep.value
    const revisitedStepAtSave = revisitedStep.value
    await persist(action, {
      ...options,
      onRecomputed: () => {
        if(revisitedStep.value === revisitedStepAtSave) {
          advanceFromStep(savedStep)
        }
      },
    })
  }

  // The logo is uploaded as soon as it is cropped and nothing in the saved data
  // marks this step done (see isOnboardingStepSatisfied), so Continue stores the
  // date format and records the step
  async function completeWorkspaceCustomization(dateFormat: DateFormatType): Promise<void> {
    const workspace = workspaceStore.currentWorkspace
    if(!workspace) {
      return
    }
    await persistStep(async () => {
      if(dateFormat !== workspace.dateFormat) {
        await workspaceApiStore.updateWorkspace(workspace.id, undefined, workspace.title, dateFormat, undefined)
      }
      await workspaceApiStore.skipOnboardingStep(workspace.id, OnboardingStepType.WorkspaceCustomization)
    })
  }

  async function saveWorkspaceLogo(logo: Blob): Promise<void> {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return
    }
    await persist(async () => {
      await uploadFile<IUpdateWorkspaceLogoRequestInterfaces['response']>({
        targetType: FileUploadTargetType.WorkspaceLogo,
        target: {workspaceId},
        file: new File([logo], 'logo.png', {type: logo.type || 'image/png'}),
        legacyUpload: () => workspaceApiStore.updateLogo(workspaceId, logo),
      })
    })
  }

  async function saveCompanyInfo(employeeRange?: OnboardingEmployeeRangeType, teamRange?: OnboardingTeamRangeType): Promise<void> {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return
    }
    await persistStep(() => workspaceApiStore.updateOnboardingCompanyInfo(workspaceId, employeeRange, teamRange))
  }

  async function saveCoreAreas(values: OnboardingCompanyCoreAreaType[]): Promise<void> {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return
    }
    await persistStep(() => workspaceApiStore.updateOnboardingCoreAreas(workspaceId, values))
  }

  async function saveSolutions(values: OnboardingSolutionType[]): Promise<void> {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return
    }
    await persistStep(() => workspaceApiStore.updateOnboardingSolutions(workspaceId, values))
  }

  async function sendInvites(mailAddresses: string[]): Promise<IWorkspaceInviteResult[] | null> {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId || mailAddresses.length === 0) {
      return null
    }
    return persistWithResult<IWorkspaceInviteResult[] | null>(async () => {
      const {inviteResults} = await workspaceInvitationApiStore.inviteMultipleUsers(workspaceId, mailAddresses, [])
      return inviteResults ?? []
    }, null)
  }

  async function createInviteLink(): Promise<string | null> {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return null
    }
    return persistWithResult<string | null>(async () => {
      const {url} = await workspaceInvitationApiStore.createInviteLink(workspaceId, [])
      return url ?? null
    }, null)
  }

  async function generateWorkspacePlan(prompt: string, locale: LocaleType): Promise<IOnboardingWorkspacePlanGroup[]> {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return []
    }
    return persistWithResult(async () => {
      const {groups} = await workspaceApiStore.generateOnboardingPlan(workspaceId, prompt, locale)
      return groups ?? []
    }, [])
  }

  async function saveWorkspacePlan(plan: IPendingOnboardingPlan): Promise<void> {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return
    }
    await persistStep(() => workspaceApiStore.updateOnboardingPlan(workspaceId, plan))
  }

  async function saveRoleAndDiscover(role?: OnboardingUserRoleType, discoverSource?: OnboardingDiscoverSourceType): Promise<void> {
    await persistStep(() => meApiStore.updateOnboardingRoleAndDiscover(role, discoverSource), {syncUser: true})
  }

  async function saveAvatar(avatar: File | Blob): Promise<void> {
    await persist(() => meApiStore.updateAvatar(avatar), {syncUser: true})
  }

  async function saveProfile(nick: string): Promise<void> {
    await persistStep(() => meApiStore.updateNick(nick), {syncUser: true})
  }

  async function skipStep(step: OnboardingStepType): Promise<void> {
    if(isUserLevelOnboardingStep(step)) {
      await persistStep(() => meApiStore.skipOnboardingStep(step), {syncUser: true})
      return
    }
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return
    }
    await persistStep(() => workspaceApiStore.skipOnboardingStep(workspaceId, step))
  }

  async function skipCurrentStep(): Promise<void> {
    if(!currentStep.value) {
      return
    }
    await skipStep(currentStep.value)
  }

  return {
    pendingSteps,
    isComplete,
    isKnown,
    isLoading,
    loadedWorkspaceId,
    saveErrorMessage,
    successMessage,
    currentStep,
    firstPendingStep,
    visibleSteps,
    doneSteps,
    isRevisiting,
    isFinalStep,
    isPersisting,
    canGoBack,
    canGoToStep,
    goToStep,
    goBack,
    goForward,
    refreshState,
    recomputeFromStores,
    showSuccessMessage,
    completeWorkspaceCustomization,
    saveWorkspaceLogo,
    saveCompanyInfo,
    saveCoreAreas,
    saveSolutions,
    sendInvites,
    createInviteLink,
    generateWorkspacePlan,
    saveWorkspacePlan,
    saveRoleAndDiscover,
    saveAvatar,
    saveProfile,
    skipStep,
    skipCurrentStep,
  }
})
